import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Refus définitif de la session : le jeton ne doit plus servir.
 *   • 401 : jeton invalide, expiré, compte inexistant ;
 *   • 403 marqué `acces_externe` : accès d'un intervenant externe expiré, suspendu, terminé, à venir,
 *     sans engagement de confidentialité ou sans fonction IPM active (message daté du serveur) ;
 *   • 403 `compte_bloque` : compte bloqué.
 * Un autre 403 (première connexion à terminer, route hors périmètre) ne déconnecte pas.
 * Une panne réseau ou une erreur 500 ne déconnecte pas non plus (le jeton est gardé).
 */
function refusDeSession(err) {
  const statut = err?.response?.status;
  const data = err?.response?.data || {};
  // Revue de sécurité (P1) : jeton antérieur à un changement ou à une réinitialisation du mot de
  // passe → 401 SESSION_EXPIREE, dont le message s'affiche sur la page de connexion.
  if (statut === 401) return { message: data.code === 'SESSION_EXPIREE' ? (data.message || null) : null, code: data.code || null, bloque: false };
  if (statut === 403 && (data.acces_externe === true || data.compte_bloque === true)) {
    return { message: data.message || 'Votre accès a été refusé. Contactez le service RH.', code: data.code || null, bloque: data.compte_bloque === true };
  }
  return null;
}

export const AuthProvider = ({ children }) => {
  const [employe, setEmploye] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('sogas_token') || null);
  // Motif de la dernière déconnexion imposée par le serveur, affiché sur la page de connexion.
  const [motifDeconnexion, setMotifDeconnexion] = useState(null);

  const nettoyerSession = () => {
    localStorage.removeItem('sogas_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setEmploye(null);
  };

  useEffect(() => {
    const stored = localStorage.getItem('sogas_token');
    if (stored) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${stored}`;
      fetchMe();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchMe = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`);
      setEmploye(res.data.employe);
    } catch (err) {
      // Refus définitif (accès expiré, suspendu, terminé, jeton invalide…) : déconnexion propre, le
      // message du serveur s'affiche sur la page de connexion. Une panne passagère garde le jeton.
      const refus = refusDeSession(err);
      if (refus) {
        nettoyerSession();
        if (refus.message) setMotifDeconnexion(refus);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (matricule, password) => {
    setMotifDeconnexion(null);
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, { matricule, password });
      const { token: tok, employe: emp, premier_connexion } = res.data;
      localStorage.setItem('sogas_token', tok);
      axios.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
      setToken(tok);
      setEmploye(emp);
      return { ...emp, premier_connexion };
    } catch (err) {
      // Connexion refusée pour un motif d'accès : aucun ancien jeton ne doit rester utilisable.
      if (refusDeSession(err) && err?.response?.status === 403) nettoyerSession();
      throw err;
    }
  };

  const logout = () => {
    nettoyerSession();
  };

  /**
   * Nouveau jeton renvoyé par le serveur (changement de mot de passe : les jetons antérieurs ne
   * sont plus acceptés). Stocké comme à la connexion, pour que l'utilisateur reste connecté.
   */
  const remplacerJeton = (tok) => {
    if (!tok) return;
    localStorage.setItem('sogas_token', tok);
    axios.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
    setToken(tok);
  };

  const effacerMotifDeconnexion = () => setMotifDeconnexion(null);

  const hasPermission = (key) => {
    return employe?.permissions_effectives?.[key] === true;
  };

  return (
    <AuthContext.Provider value={{
      employe, token, loading, login, logout, hasPermission, rafraichir: fetchMe, remplacerJeton,
      motifDeconnexion, effacerMotifDeconnexion,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
